import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSubdomain } from "app/utils/generic.helper";
import { Button, Input, Tag, Modal, Select, Upload, Progress, Tooltip, message } from "antd";
import {
  CheckCircleOutlined,
  UploadOutlined,
  WarningOutlined,
  CloseOutlined,
  MailOutlined,
  DeleteFilled,
  EyeFilled,
  ExclamationCircleFilled,
  CopyOutlined,
  AppstoreOutlined,
  DownOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { AgTableClient } from "app/shared/AgTable";
import PageHeader from "app/shared/PageHeader";
import HotelOnboardingDrawer from "./HotelOnboardingDrawer";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import CleartripLogo from "static/images/Cleartrip_Original.svg.webp";
import SunPharmaLogo from "static/images/sun-pharma-logo-png.png";
import YatraLogo from "static/images/Yatra_logo.svg.webp";
import AtpiLogo from "static/images/atpi-ltd-logo-vector.svg";
import MmtLogo from "static/images/mmtNew.png";
import ZiptrripLogo from "static/images/ZipTrripLogo.png";
import FcmLogo from "static/images/fcmimages.png";
import BcdLogo from "static/images/bcdTravel.png";
import TravelPlusLogo from "static/images/TravelPlus.png";
import PytLogo from "static/images/pytimages.png";
import EbixLogo from "static/images/ebixNew.png";
import SamsungLogo from "static/images/samsung.jpg";
import EmtLogo from "static/images/emtimages.jpeg";
import Quest2TravelLogo from "static/images/q2Travel.png";
import BalmerLogo from "static/images/balmer.png";
import "./ClearTripTable.scss";

// Kept in sync with LOCAL_DOMAIN_LOGOS in LoginCleartrip so the same logo
// that greeted the hotel at sign-in also appears in this header.
const LOCAL_DOMAIN_LOGOS = {
  cleartrip: CleartripLogo,
  sunpharma: SunPharmaLogo,
  myyatra: YatraLogo,
  atpi: AtpiLogo,
  mmt: MmtLogo,
  ziptrrip: ZiptrripLogo,
  fcm: FcmLogo,
  bcd: BcdLogo,
  travelplus: TravelPlusLogo,
  pyt: PytLogo,
  ebix: EbixLogo,
  samsung: SamsungLogo,
  emt: EmtLogo,
  quest2travel: Quest2TravelLogo,
  balmer: BalmerLogo,
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

const API_INVOICE_UPLOAD_DATA = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice-upload-data";
const API_INVOICE_UPLOAD_CONFIG = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice-upload-config";
const API_INVOICE_SUMMARY = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/summary";
const API_INVOICE_BULK_UPLOAD = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/bulk";
const API_INVOICE_SINGLE_UPLOAD = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/single";
const API_DELETE_INVOICE = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/invoice/upload/delete";
const API_SAVE_REMARK = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/remarks/save";
const API_ENABLEMENT_STATUS = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/enablementStatus";
const API_ONBOARDING_LEGAL_NAME = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/onBoarding/legalName";
const API_PRESIGNED_URL = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/preSignedUrl";

function mapInvoiceRow(item) {
  const uploaded =
    item.uploadSingleStatus === true ||
    (Array.isArray(item.uploadLinks) && item.uploadLinks.length > 0);
  return {
    ...item,
    bookingId: item.email_bookingId,
    sharableBookingId: item.email_sharableBookingId,
    vendorName: item.email_hotel_name,
    city: item.email_hotel_city,
    state: item.email_hotel_state,
    gstin: item.email_hotel_gstin,
    client: item.company_name || item.booking_client_id,
    revenue: item.revenue ?? 0,
    gstAmount: item.gstAmount ?? 0,
    status: uploaded ? "uploaded" : "upload",
    remarks: item.remarks,
  };
}


function isPdfUrl(url) {
  return /\.pdf(\?|$)/i.test(url || "");
}

function InvoicePreviewModal({ open, urls, loading, onClose }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title="Invoice Preview"
      className="ctt-invoice-preview-modal"
    >
      {loading ? (
        <div className="ctt-invoice-preview-loading">Loading invoice...</div>
      ) : (
        (urls || []).map((url, i) =>
          isPdfUrl(url) ? (
            <iframe key={i} src={url} title={`invoice-${i}`} className="ctt-invoice-preview-frame" />
          ) : (
            <img key={i} src={url} alt={`invoice-${i}`} className="ctt-invoice-preview-image" />
          )
        )
      )}
    </Modal>
  );
}

function handleCopy(value) {
  navigator.clipboard.writeText(value);
  message.success("Copied");
}

function CopyableTag({ value }) {
  if (!value) return null;
  return (
    <Tag color="blue">
      {value}
      <Tooltip title="Copy">
        <CopyOutlined
          style={{ marginLeft: 6, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleCopy(value);
          }}
        />
      </Tooltip>
    </Tag>
  );
}

function RemarksCellRenderer({ value, data, onSave }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || "");

  function handleSave() {
    onSave(data, text);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="ctt-remark-edit">
        <Input
          size="small"
          autoFocus
          className="ctt-remark-input"
          placeholder="Add remark..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPressEnter={handleSave}
        />
        <Button size="small" type="link" className="ctt-remark-save-btn" onClick={handleSave}>
          Save
        </Button>
      </div>
    );
  }

  return (
    <span className="ctt-add-remark" onClick={() => setEditing(true)}>
      <EditOutlinedIcon className="ctt-icon-13" />
      {text || "Add remark"}
    </span>
  );
}


const GST_INVOICE_FIELDS = [
  {
    title: "Supplier Details",
    desc: "Full legal name, registered address & GSTIN of your property / entity",
  },
  {
    title: "Recipient Details",
    desc: "Corporate buyer's name, billing address & GSTIN exactly as on the booking",
  },
  {
    title: "Invoice Number",
    desc: "Unique, sequential — e.g. INV/2024-25/0173 (cannot be re-used within FY)",
  },
  {
    title: "Invoice Date",
    desc: "Date of check-out / service completion — not check-in date",
  },
  {
    title: "Place of Supply",
    desc: "State where the hotel is located — must match your GSTIN's state code",
  },
  {
    title: "HSN / SAC Code",
    desc: "SAC 9963 for accommodation services (always required)",
  },
  {
    title: "Taxable Value",
    desc: "Booking amount before GST — must match the amount on the Cleartrip booking",
  },
  {
    title: "GST Rate & Breakup",
    desc: "12% (≤ ₹7,500/night) or 18% (> ₹7,500/night). Show CGST + SGST or IGST separately.",
  },
  {
    title: "Total Invoice Value",
    desc: "Taxable value + all GST components — must be arithmetically exact",
  },
  {
    title: "Reverse Charge",
    desc: "State whether GST is payable on reverse charge basis (typically: No)",
  },
  {
    title: "Signature",
    desc: "Signed by authorised signatory of the supplier entity",
  },
];

function renderHighlighted(text) {
  if (!text) return null;
  const regex = /('[^']+'|Rs\s+[\d,]+\s*Cr)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text))) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <b key={key++} className="ctt-enablement-highlight">
        {match[0]}
      </b>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function parseFlexibleDate(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const ddmmyyyy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

const DATE_FILTER_PARAMS = {
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    const cellDate = parseFlexibleDate(cellValue);
    if (!cellDate) return -1;
    const cellDateAtMidnight = new Date(
      cellDate.getFullYear(),
      cellDate.getMonth(),
      cellDate.getDate()
    );
    if (cellDateAtMidnight < filterLocalDateAtMidnight) return -1;
    if (cellDateAtMidnight > filterLocalDateAtMidnight) return 1;
    return 0;
  },
};

const GST_INVOICE_COMMON_ERRORS = [
  "Incorrect or missing GSTIN of the corporate buyer",
  "Mismatch between the booking entity name and the invoice recipient name",
  "Wrong place of supply — must be the hotel's state, not the buyer's",
  "Missing SAC code 9963 or using a generic HSN instead",
  "Rounding errors in CGST / SGST breakdown",
  "Invoice date set before check-in or well after check-out",
  "Not generating an IRN + QR code when e-invoice is mandatory",
];

function InvoiceGuideModal({ open, onClose }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}

      closeIcon={<Button type="text" shape="circle" size="small" icon={<CloseOutlined />} />}
      title={
        <div>
          <div className="ctt-guide-title">GST Tax Invoice — Required Format</div>
          <div className="ctt-guide-subtitle">
            Mandatory fields under Section 31 of the CGST Act, 2017
          </div>
        </div>
      }
    >
      <div className="ctt-guide-body">
        {/* Why this matters */}
        <div className="ctt-guide-why-banner">
          <div className="ctt-guide-why-title">Why this matters</div>
          <div className="ctt-guide-why-text">
            An incomplete or incorrect invoice directly leads to ITC (Input Tax
            Credit) rejection for your corporate client — which damages your
            relationship and risks future bookings being routed elsewhere.
          </div>
        </div>

        {/* Mandatory Fields */}
        <div className="ctt-guide-section-label">MANDATORY INVOICE FIELDS</div>
        <div className="ctt-guide-field-list">
          {GST_INVOICE_FIELDS.map((f, i) => (
            <div key={i} className="ctt-guide-field-row">
              <CheckCircleOutlined className="ctt-guide-field-icon" />
              <div>
                <div className="ctt-guide-field-title">{f.title}</div>
                <div className="ctt-guide-field-desc">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Common Errors */}
        <div className="ctt-guide-section-label">COMMON ERRORS THAT CAUSE ITC REJECTION</div>
        <div className="ctt-guide-error-list">
          {GST_INVOICE_COMMON_ERRORS.map((error, i) => (
            <div key={i} className="ctt-guide-error-row">
              <WarningOutlined className="ctt-guide-error-icon" />
              <span className="ctt-guide-error-text">{error}</span>
            </div>
          ))}
        </div>

        {/* e-Invoice Mandate */}
        <div className="ctt-guide-fact-card">
          <div className="ctt-guide-fact-header">
            <ElectricBoltIcon className="ctt-guide-fact-icon" />
            <span className="ctt-guide-fact-title">E-Invoice Mandate</span>
          </div>
          <div className="ctt-guide-fact-text">
            Vendors with aggregate annual turnover exceeding <b>₹5 Crore</b> in
            the preceding FY must generate e-invoices on the IRP (Invoice
            Registration Portal). The <b>IRN</b> and <b>QR Code</b> must appear
            on every invoice. Failure renders the invoice invalid for ITC
            purposes.
          </div>
        </div>
      </div>
    </Modal>
  );
}

function SubmitInvoiceModal({ open, row, onClose, onSubmit, uploading }) {
  const [fileList, setFileList] = useState([]);
  const file = fileList[0];

  function handleCancel() {
    if (uploading) return;
    setFileList([]);
    onClose();
  }

  async function handleSubmit() {
    if (!file || uploading) return;
    const originFile = file.originFileObj || file;
    const success = await onSubmit(row, originFile);
    if (success) setFileList([]);
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      width={520}
      maskClosable={!uploading}
      closable={!uploading}
      title={
        <div>
          <div className="ctt-upload-modal-title">Submit GST Invoice</div>
          <div className="ctt-upload-modal-subtitle">
            Booking ID:{" "}
            <span className="ctt-upload-modal-booking-id">{row?.sharableBookingId}</span>
          </div>
        </div>
      }
      footer={[
        <Button
          key="cancel"
          className="ctt-upload-modal-cancel-btn"
          onClick={handleCancel}
          disabled={uploading}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          className="ctt-upload-modal-submit-btn"
          disabled={!file}
          loading={uploading}
          onClick={handleSubmit}
        >
          Submit Invoice
        </Button>,
      ]}
    >
      <Upload.Dragger
        className="ctt-upload-modal-drop-zone"
        accept=".pdf,.jpg,.jpeg,.png"
        maxCount={1}
        disabled={uploading}
        fileList={fileList}
        showUploadList={false}
        beforeUpload={() => false}
        onChange={({ fileList: fl }) => setFileList(fl)}
      >
        {file ? (
          <>
            <CheckCircleOutlined className="ctt-upload-modal-success-icon" />
            <div className="ctt-upload-modal-drop-main">{file.name}</div>
            <div className="ctt-upload-modal-drop-sub">
              {file.size ? (file.size / 1024 / 1024).toFixed(2) : "0.00"} MB · Click to replace
            </div>
          </>
        ) : (
          <>
            <UploadOutlined className="ctt-upload-modal-placeholder-icon" />
            <div className="ctt-upload-modal-drop-main">Drop invoice here or click to browse</div>
            <div className="ctt-upload-modal-drop-sub">PDF, JPG or PNG · Max 10 MB</div>
          </>
        )}
      </Upload.Dragger>
    </Modal>
  );
}

function BulkUploadModal({ open, onClose, onSubmit, uploading }) {
  const [fileList, setFileList] = useState([]);

  function handleCancel() {
    if (uploading) return;
    setFileList([]);
    onClose();
  }

  async function handleSubmit() {
    if (!fileList.length || uploading) return;
    const success = await onSubmit(fileList);
    if (success) setFileList([]);
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      width={520}
      title="Bulk Upload Invoices"
      maskClosable={!uploading}
      closable={!uploading}
      footer={[
        <Button
          key="cancel"
          className="ctt-upload-modal-cancel-btn"
          onClick={handleCancel}
          disabled={uploading}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          className="ctt-upload-modal-submit-btn"
          disabled={!fileList.length}
          loading={uploading}
          onClick={handleSubmit}
        >
          Upload {fileList.length || ""} {fileList.length === 1 ? "File" : "Files"}
        </Button>,
      ]}
    >
      <Upload.Dragger
        className="ctt-upload-modal-drop-zone"
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        disabled={uploading}
        fileList={fileList}
        beforeUpload={() => false}
        onChange={({ fileList: fl }) => setFileList(fl)}
      >
        <UploadOutlined className="ctt-upload-modal-placeholder-icon" />
        <div className="ctt-upload-modal-drop-main">Drop invoices here or click to browse</div>
        <div className="ctt-upload-modal-drop-sub">PDF, JPG or PNG · Multiple files supported</div>
      </Upload.Dragger>
    </Modal>
  );
}

export default function ClearTripTable() {
  const navigate = useNavigate();
  const domain = getSubdomain();

  const [activeTab, setActiveTab] = useState("pending");
  const [searchText, setSearchText] = useState("");
  const [showInvoiceGuide, setShowInvoiceGuide] = useState(false);
  const [selectedClient, setSelectedClient] = useState("All Clients");
  const [tableData, setTableData] = useState([]);
  const [uploadModal, setUploadModal] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [uploadConfig, setUploadConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [uniqueClients, setUniqueClients] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [singleUploading, setSingleUploading] = useState(false);
  const [enablementStatus, setEnablementStatus] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [legalName, setLegalName] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState({ open: false, urls: [], loading: false });

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handler = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  useEffect(() => {
    const fetchEnablementStatus = async () => {
      try {
        const res = await fetch(API_ENABLEMENT_STATUS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hotel_gstin: localStorage.getItem("hotelGstin") }),
        });
        const result = await res.json();
        setEnablementStatus(result);
      } catch (error) {
        setEnablementStatus(null);
      }
    };
    fetchEnablementStatus();
  }, []);

  useEffect(() => {
    const fetchLegalName = async () => {
      const gstin = localStorage.getItem("hotelGstin");
      if (!gstin) return;
      try {
        const res = await fetch(API_ONBOARDING_LEGAL_NAME, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ gstin }),
        });
        const result = await res.json().catch(() => null);
        if (result?.legalName) setLegalName(result.legalName);
      } catch (error) {
        // Best-effort; header falls back to email_hotel_name / localStorage.
      }
    };
    fetchLegalName();
  }, []);

  const fetchSummary = async (clientId) => {
    try {
      const res = await fetch(API_INVOICE_SUMMARY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gstin: localStorage.getItem("hotelGstin"),
          domain: getSubdomain(),
          ...(clientId && clientId !== "All Clients" ? { clientId } : {}),
        }),
      });
      const response = await res.json();
      setSummary(response?.summary ?? response?.data ?? response);
    } catch (error) {
      setSummary(null);
    }
  };

  useEffect(() => {
    fetchSummary(selectedClient);
  }, [selectedClient]);

  const fetchInvoiceData = async () => {
    try {
      const res = await fetch(API_INVOICE_UPLOAD_DATA, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gstin: localStorage.getItem("hotelGstin"),
          domain: getSubdomain(),
        }),
      });
      const response = await res.json();
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setTableData(data.map(mapInvoiceRow));
      setUniqueClients(Array.isArray(response?.uniqueClients) ? response.uniqueClients : []);
    } catch (error) {
      setTableData([]);
    }
  };

  useEffect(() => {
    fetchInvoiceData();
  }, []);

  useEffect(() => {
    const fetchUploadConfig = async () => {
      try {
        const domain = getSubdomain();
        const res = await fetch(`${API_INVOICE_UPLOAD_CONFIG}?domain=${domain}`);
        const response = await res.json();
        setUploadConfig(response?.data ?? response);
      } catch (error) {
        setUploadConfig(null);
      }
    };
    fetchUploadConfig();
  }, []);

  const logoUrl = LOCAL_DOMAIN_LOGOS[domain] || uploadConfig?.find((c) => c.logo)?.logo;
  const [logoBoxHeight, setLogoBoxHeight] = useState(28);

  const handleLogoLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (!naturalWidth || !naturalHeight) return;
    const isSquareish = naturalWidth / naturalHeight < 1.4;
    setLogoBoxHeight(isSquareish ? 32 : 28);
  }

  const handleUploadClick = (row) => {
    setUploadModal(row);
  };

  const handleViewInvoice = async (row) => {
    if (!Array.isArray(row?.uploadLinks) || !row.uploadLinks.length) {
      message.error("Invoice file not found.");
      return;
    }
    setInvoicePreview({ open: true, urls: [], loading: true });
    try {
      const urls = await Promise.all(
        row.uploadLinks.map(async (link) => {
          const res = await fetch(API_PRESIGNED_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ s3Url: link }),
          });
          const result = await res.json();
          return result?.s3Url || result?.url || link;
        })
      );
      setInvoicePreview({ open: true, urls, loading: false });
    } catch (error) {
      message.error("Failed to load invoice.");
      setInvoicePreview({ open: false, urls: [], loading: false });
    }
  };

  const handleBulkUpload = async (fileList) => {
    const pendingRows = tableData.filter((r) => r.status === "upload");
    const uniqueClientIds =
      summary?.uniqueClientIds ||
      Array.from(new Set(tableData.map((d) => d.booking_client_id).filter(Boolean)));

    const formData = new FormData();
    const ts = Date.now();
    fileList.forEach((item, i) => {
      const file = item.originFileObj || item;
      const ext = file.name.split(".").pop();
      formData.append("files", file);
      formData.append("filename", `bulk_${ts}_${i}.${ext}`);
    });
    formData.append("bucket_name", "fink-followup-data");
    uniqueClientIds.forEach((id) => formData.append("clientId", id));
    formData.append("email_hotel_gstin", localStorage.getItem("hotelGstin") || "");
    formData.append(
      "uploadRemarks",
      fileList.length < pendingRows.length ? "partial upload" : "complete"
    );

    try {
      setBulkUploading(true);
      const res = await fetch(API_INVOICE_BULK_UPLOAD, { method: "POST", body: formData });
      const result = await res.json();
      if (res.ok || result?.status) {
        setBulkUploadOpen(false);
        await Promise.all([fetchInvoiceData(), fetchSummary()]);
        return true;
      }
      message.error(result?.message || "Failed to upload invoices.");
      return false;
    } catch (error) {
      message.error("Failed to upload invoices.");
      return false;
    } finally {
      setBulkUploading(false);
    }
  };

  const handleSubmitInvoice = async (row, file) => {
    const ext = file.name.split(".").pop();
    const filename = `${row?.sharableBookingId || row?.email_bookingId || "booking"}_${Date.now()}.${ext}`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("bucket_name", "fink-followup-data");
    formData.append("filename", filename);
    formData.append("clientId", row?.booking_client_id || "");
    formData.append("email_sharableBookingId", row?.sharableBookingId || "");
    formData.append("id", row?._id || "");

    try {
      setSingleUploading(true);
      const res = await fetch(API_INVOICE_SINGLE_UPLOAD, { method: "POST", body: formData });
      const result = await res.json();
      if (res.ok || result?.status) {
        setUploadModal(null);
        await Promise.all([fetchInvoiceData(), fetchSummary()]);
        return true;
      }
      message.error(result?.message || "Failed to upload invoice.");
      return false;
    } catch (error) {
      message.error("Failed to upload invoice.");
      return false;
    } finally {
      setSingleUploading(false);
    }
  };

  const handleSaveRemark = async (row, text) => {
    try {
      const res = await fetch(API_SAVE_REMARK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: row._id,
          remarks: text,
          email_sharableBookingId: row.sharableBookingId,
        }),
      });
      const result = await res.json();
      if (res.ok || result?.status) {
        setTableData((prev) =>
          prev.map((r) =>
            r.sharableBookingId === row.sharableBookingId ? { ...r, remarks: text } : r
          )
        );
      } else {
        message.error(result?.message || "Failed to save remark.");
      }
    } catch (error) {
      message.error("Failed to save remark.");
    }
  };

  const handleDeleteInvoice = async (row) => {
    try {
      const res = await fetch(API_DELETE_INVOICE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ _id: row._id, uploadLinks: row.uploadLinks }),
      });
      const result = await res.json();
      if (res.ok || result?.status) {
        setTableData((prev) =>
          prev.map((r) =>
            r.sharableBookingId === row.sharableBookingId
              ? { ...r, status: "upload", uploadSingleStatus: false, uploadLinks: [] }
              : r
          )
        );
      } else {
        message.error(result?.message || "Failed to delete invoice.");
      }
    } catch (error) {
      message.error("Failed to delete invoice.");
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Booking ID",
        field: "email_sharableBookingId",
        minWidth: 130,
        filter: "agTextColumnFilter",
        cellDataType: "text",
        pinned: "right",
        enableRowGroup: true,
        cellRenderer: (params) => {
          if (params.node.group || !params.value) return params.value;
          return <CopyableTag value={params.value} />;
        },
      },
      {
        headerName: "Client Details",
        children: [
          {
            headerName: "Client Name",
            field: "client",
            minWidth: 250,
            filter: "agTextColumnFilter",
            enableRowGroup: true,
            columnGroupShow: "close",
          },
          {
            headerName: " Client GSTIN",
            field: "gstin",
            minWidth: 170,
            enableRowGroup: true,
            filter: "agTextColumnFilter",
            columnGroupShow: "close",
            cellRenderer: (params) => {
              if (params.node.group || !params.value) return params.value;
              return <CopyableTag value={params.value} />;
            },
          },


        ],
      },
      {
        headerName: "GST Amount",
        field: "totalGST",
        minWidth: 130,
        enableRowGroup: true,
        filter: "agNumberColumnFilter",
        formatType: "AMOUNT",
      },
      {
        headerName: "Hotel Details",
        children: [
          {
            headerName: "Hotel Name",
            field: "email_hotel_name",
            minWidth: 250,
            enableRowGroup: true,
            filter: "agTextColumnFilter",
            formatType: "ENTITY",
            columnGroupShow: "close",
          },
          {
            headerName: " Hotel GSTIN",
            field: "email_hotel_gstin",
            minWidth: 170,
            enableRowGroup: true,
            filter: "agTextColumnFilter",
            formatType: "CHIP",
            formatProps: { chipColor: "blue" },
            columnGroupShow: "open",
          },
          {
            headerName: " City",
            field: "email_hotel_city",
            minWidth: 170,
            enableRowGroup: true,
            filter: "agTextColumnFilter",
            columnGroupShow: "open",
          },
          {
            headerName: " State",
            field: "email_hotel_state",
            minWidth: 170,
            enableRowGroup: true,
            filter: "agTextColumnFilter",
            columnGroupShow: "open",
          },
          {
            headerName: "Hotel Address",
            field: "email_hotel_address",
            minWidth: 220,
            filter: "agTextColumnFilter",
            columnGroupShow: "open",
          },
        ]
      },
      {
        headerName: "Traveller Details",
        children: [
          {
            headerName: "Traveller Name",
            field: "email_travellers_info",
            minWidth: 180,
            enableRowGroup: true,
            valueGetter: (params) => {
              if (!Array.isArray(params.data?.email_travellers_info)) return "-";
              const names = params.data.email_travellers_info
                .map((t) => t?.name?.trim())
                .filter((name) => name && !/^undefined(\s+undefined)*$/i.test(name));
              return names.length ? names.join(", ") : "-";
            },
          },
          {
            headerName: "Traveller Email(s)",
            field: "email",
            minWidth: 270,
            enableRowGroup: true,
            valueGetter: (params) =>
              Array.isArray(params.data?.email) && params.data.email.length
                ? params.data.email.map((e) => (e || "").toLowerCase()).join(", ")
                : "-",
            cellRenderer: (params) => {
              const emails = Array.isArray(params.data?.email)
                ? params.data.email.map((e) => (e || "").toLowerCase())
                : [];
              if (!emails.length) return "-";
              const [first, ...rest] = emails;
              return (
                <Tooltip title={emails.join(", ")}>
                  <span style={{ cursor: "pointer" }}>
                    <Tag icon={<MailOutlined />}>{first}</Tag>
                    {rest.length > 0 && (
                      <span style={{ marginLeft: 4, color: "#000" }}>+{rest.length}</span>
                    )}
                  </span>
                </Tooltip>
              );
            },
          },
        ]
      },
      {
        headerName: "Booking Date",
        field: "email_bookingdate",
        minWidth: 160,
        enableRowGroup: true,
        filter: "agDateColumnFilter",
        filterParams: DATE_FILTER_PARAMS,
        columnGroupShow: "open",
        valueFormatter: (params) => {
          const date = parseFlexibleDate(params.value);
          return date ? date.toLocaleDateString() : "—";
        },
      },
      {
        headerName: "Check-in Date",
        field: "email_checkindate",
        minWidth: 160,
        enableRowGroup: true,
        columnGroupShow: "open",
        filter: "agDateColumnFilter",
        filterParams: DATE_FILTER_PARAMS,
        valueFormatter: (params) => {
          const date = parseFlexibleDate(params.value);
          return date ? date.toLocaleDateString() : "—";
        },
      },
      {
        headerName: "Check-out Date",
        field: "email_checkoutdate",
        minWidth: 160,
        enableRowGroup: true,
        columnGroupShow: "open",
        filter: "agDateColumnFilter",
        filterParams: DATE_FILTER_PARAMS,
        valueFormatter: (params) => {
          const date = parseFlexibleDate(params.value);
          return date ? date.toLocaleDateString() : "—";
        },
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 100,
        sortable: true,
        filter: true,
        pinned: "right",
        cellRenderer: (params) => {
          if (params.node.group) {
            return "";
          }
          return (
            <div className="status-cell">
              {params.value === "uploaded" ? (
                <>
                  <Tooltip title="View invoice">
                    <Button
                      size="small"
                      className="ctt-view-invoice-btn"
                      icon={<EyeFilled className="ctt-icon-13" />}
                      onClick={() => handleViewInvoice(params.data)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete invoice">
                    <Button
                      size="small"
                      className="ctt-delete-invoice-btn"
                      icon={<DeleteFilled className="ctt-icon-13" />}
                      onClick={() => handleDeleteInvoice(params.data)}
                    />
                  </Tooltip>
                </>
              ) : (
                <Button
                  type="primary"
                  size="small"
                  className="ctt-row-upload-btn"
                  icon={<UploadOutlined className="ctt-icon-13" />}
                  onClick={() => handleUploadClick(params.data)}
                >
                </Button>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Remarks",
        field: "remarks",
        minWidth: 210,
        pinned: "right",
        hide: true,
        cellRenderer: (params) => {
          if (params.node.group) {
            return "";
          }
          return (
            <RemarksCellRenderer value={params.value} data={params.data} onSave={handleSaveRemark} />
          );
        },
      },
    ],
    [handleUploadClick, handleSaveRemark]
  );

  const clientOptions = useMemo(() => {
    if (uniqueClients.length) {
      return [
        { label: "All Clients", value: "All Clients" },
        ...uniqueClients.map((c) => ({
          label: c.workspace_name || c.client_id,
          value: c.client_id,
        })),
      ];
    }
    const uniqueClientIds =
      summary?.uniqueClientIds ||
      Array.from(new Set(tableData.map((d) => d.booking_client_id).filter(Boolean)));
    return [
      { label: "All Clients", value: "All Clients" },
      ...uniqueClientIds.map((id) => ({ label: id, value: id })),
    ];
  }, [uniqueClients, summary, tableData]);

  const clientScopedData = useMemo(() => {
    if (selectedClient === "All Clients") return tableData;
    return tableData.filter((d) => d.booking_client_id === selectedClient);
  }, [tableData, selectedClient]);

  const filteredData = useMemo(() => {
    let data = clientScopedData;
    if (activeTab === "uploaded")
      data = data.filter((d) => d.status === "uploaded");
    if (activeTab === "pending")
      data = data.filter((d) => d.status === "upload");
    if (searchText) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (d) =>
          String(d.sharableBookingId ?? "").toLowerCase().includes(q) ||
          String(d.client ?? "").toLowerCase().includes(q) ||
          String(d.city ?? "").toLowerCase().includes(q) ||
          String(d.vendorName ?? "").toLowerCase().includes(q) ||
          String(d.gstin ?? "").toLowerCase().includes(q)
      );
    }
    return data;
  }, [activeTab, searchText, clientScopedData]);

  const uploadedCount = clientScopedData.filter((d) => d.status === "uploaded").length;
  const pendingCount = clientScopedData.filter((d) => d.status === "upload").length;

  const compliantCount = summary?.uploadedCount ?? uploadedCount;
  const totalBookingsCount = summary?.totalInvoices ?? clientScopedData.length;
  const summaryPendingCount = summary?.pendingCount ?? pendingCount;
  const totalRevenue = summary?.totalRevenue ?? 0;
  const gstAtRisk = summary?.amountAtRisk ?? 0;
  const compliancePercent =
    totalBookingsCount > 0 ? Math.round((compliantCount / totalBookingsCount) * 100) : 0;

  const tabs = [
    {
      key: "all",
      count: clientScopedData.length,
      label: "ALL INVOICES",
      sub: "total bookings",
      icon: <ReceiptLongIcon className="ctt-icon-16" />,
    },
    {
      key: "uploaded",
      count: uploadedCount,
      label: "UPLOADED",
      sub: "GSTR-2B ready",
      icon: <CheckCircleOutlinedIcon className="ctt-icon-16" />,
    },
    {
      key: "pending",
      count: pendingCount,
      label: "PENDING",
      sub: "ITC at risk",
      icon: <WarningAmberIcon className="ctt-icon-16" />,
    },
  ];

  return (
    <div className="ctt-page">
      {/* HEADER */}
      <PageHeader
        noAuth
        leftActions={
          <div className="ctt-logo-area">
            <span className="ctt-cleartrip-logo-clip" style={{ height: logoBoxHeight }}>
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt=""
                  className="ctt-cleartrip-logo-img"
                  onLoad={handleLogoLoad}
                />
              )}
            </span>
            <div className="ctt-header-vendor-info">
              <div className="ctt-header-vendor-name">
                {legalName || tableData[0]?.email_hotel_name || localStorage.getItem("hotelName")}
              </div>
              <div className="ctt-header-vendor-gstin">
                GSTIN: {localStorage.getItem("hotelGstin")}
              </div>
            </div>
            {domain === "app" && (
              <div className="ctt-portal-select">
                <div className="ctt-portal-select-value">
                  <AppstoreOutlined className="ctt-portal-select-icon" />
                  <Select
                    className="ctt-client-select"
                    value={selectedClient}
                    onChange={setSelectedClient}
                    options={clientOptions}
                    variant="borderless"
                    popupMatchSelectWidth={false}
                    suffixIcon={<DownOutlined />}
                  />
                </div>
              </div>
            )}
          </div>
        }
        rightActions={
          <div className="ctt-header-right">
            <Button
              type="primary"
              className="ctt-onboard-hotel-btn"
              icon={<PlusOutlined />}
              onClick={() => setOnboardingOpen(true)}
            >
              Complete Onboarding
            </Button>
            <Button
              className="ctt-invoice-guide-btn"
              icon={<InfoOutlinedIcon className="ctt-icon-11" />}
              onClick={() => setShowInvoiceGuide(true)}
            >
              Invoice Guide — action needed
            </Button>
            <Button
              type="primary"
              className="ctt-sign-out-btn"
              icon={<LogoutIcon className="ctt-icon-16" />}
              onClick={() => navigate("/login/hotelInvoices")}
            >
              Sign Out
            </Button>
          </div>
        }
      />

      <div className="ctt-content">

        {/* STATS ROW */}
        <div className="ctt-stats-row">
          <div className="ctt-stat-card">
            <Progress
              className="ctt-compliance-donut"
              type="circle"
              percent={compliancePercent}
              size={60}
              strokeWidth={7}
              strokeColor={summaryPendingCount === 0 ? "#27ae60" : "#e53935"}
              format={(pct) => `${pct}%`}
            />
            <div className="ctt-stat-info">
              <div className="ctt-stat-label">COMPLIANCE</div>
              <div className="ctt-stat-main">
                <b>{compliantCount}</b> of {totalBookingsCount}
              </div>
              <div
                className={cx(
                  "ctt-stat-warn",
                  summaryPendingCount === 0 && "ctt-stat-warn--ok"
                )}
              >
                {summaryPendingCount === 0 ? (
                  <CheckCircleOutlinedIcon className="ctt-icon-13" />
                ) : (
                  <WarningAmberIcon className="ctt-icon-13" />
                )}
                {summaryPendingCount} pending
              </div>
            </div>
          </div>
          <div className="ctt-stat-divider" />
          <div className="ctt-stat-card">
            <div className="ctt-stat-icon-circle">
              <TrendingUpIcon className="ctt-revenue-icon" />
            </div>
            <div className="ctt-stat-info">
              <div className="ctt-stat-label">TOTAL REVENUE</div>
              <div className={cx("ctt-stat-main", "ctt-stat-main--lg")}>
                ₹{totalRevenue.toLocaleString("en-IN")}
              </div>
              <div className="ctt-stat-sub">from partner bookings</div>
            </div>
          </div>
          <div className="ctt-stat-divider" />
          <div className="ctt-stat-card">
            <div className={cx("ctt-stat-icon-circle", "ctt-stat-icon-circle--danger")}>
              <WarningOutlined className="ctt-risk-icon" />
            </div>
            <div className="ctt-stat-info">
              <div className="ctt-stat-label">GST AT RISK</div>
              <div className={cx("ctt-stat-main", "ctt-stat-main--danger")}>
                ₹{gstAtRisk.toLocaleString("en-IN")}
              </div>
              <div className="ctt-stat-sub">ITC blocked if unresolved</div>
            </div>
          </div>
        </div>

        {/* TABLE CARD */}
        <div className="ctt-table-card">
          {/* TABS */}
          <div className="ctt-tabs">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  className={cx(
                    "ctt-tab-btn",
                    `ctt-tab-btn--${tab.key}`,
                    isActive && "ctt-tab-btn--active"
                  )}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span className="ctt-tab-icon">
                    {tab.icon}
                  </span>
                  <div>
                    <div className="ctt-tab-count">
                      {tab.count}
                    </div>
                    <div className="ctt-tab-label">
                      {tab.label}
                    </div>
                    <div className="ctt-tab-sub">
                      {tab.sub}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* TOOLBAR */}
          <div className="ctt-toolbar">
            <span className="ctt-showing-text">
              Showing <b>{filteredData.length}</b> bookings
              {activeTab !== "all" && (
                <span className="ctt-filter-note">
                  {" · filtered by "}
                  <span className={cx("ctt-filter-tag", `ctt-filter-tag--${activeTab}`)}>
                    {activeTab}
                  </span>
                </span>
              )}
            </span>
            <div className="ctt-toolbar-right">
              <Input
                className="ctt-search-box"
                prefix={<SearchIcon className="ctt-search-icon" />}
                placeholder="Search bookings..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {/* <Button
                className="ctt-bulk-upload-btn"
                icon={<FileUploadOutlinedIcon className="ctt-icon-15" />}
                onClick={() => setBulkUploadOpen(true)}
              >
                Bulk Upload
              </Button> */}
            </div>
          </div>

          {/* AG GRID */}
          <div className="ctt-grid-wrapper">
            <AgTableClient
              groupPanel
              rowData={filteredData}
              columnDefs={columnDefs}
              //   hideToolbar
              autoResize
            />
          </div>
        </div>
      </div>
      <HotelOnboardingDrawer
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        hotelName={legalName || tableData[0]?.email_hotel_name || localStorage.getItem("hotelName")}
      />
      <InvoiceGuideModal open={showInvoiceGuide} onClose={() => setShowInvoiceGuide(false)} />
      <SubmitInvoiceModal
        open={!!uploadModal}
        row={uploadModal}
        onClose={() => setUploadModal(null)}
        onSubmit={handleSubmitInvoice}
        uploading={singleUploading}
      />
      <BulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSubmit={handleBulkUpload}
        uploading={bulkUploading}
      />
      <InvoicePreviewModal
        open={invoicePreview.open}
        urls={invoicePreview.urls}
        loading={invoicePreview.loading}
        onClose={() => setInvoicePreview({ open: false, urls: [], loading: false })}
      />
      <Modal
        open={enablementStatus?.einvoiceStatus === "Yes"}
        onCancel={() => setEnablementStatus(null)}
        footer={null}
        closable={false}
        width={470}
        centered
        className="ctt-enablement-modal"
      >
        <div className="ctt-enablement-header">
          <div className="ctt-enablement-badge">
            <ExclamationCircleFilled />
          </div>
          <div className="ctt-enablement-title">e-Invoicing Enabled</div>
        </div>
        <div className="ctt-enablement-body">
          <p className="ctt-enablement-text">
            {renderHighlighted(enablementStatus?.einvoiceEnable)}
          </p>
          <div className="ctt-enablement-checklist">
            <div className="ctt-enablement-check-row">
              <CheckCircleOutlined />
              <span>The IRN (Invoice Reference Number) must appear on every invoice.</span>
            </div>
            <div className="ctt-enablement-check-row">
              <CheckCircleOutlined />
              <span>The QR Code must appear on every invoice.</span>
            </div>
          </div>
          <Button
            type="primary"
            block
            className="ctt-enablement-btn"
            onClick={() => setEnablementStatus(null)}
          >
            Acknowledge
          </Button>
        </div>
      </Modal>
    </div>
  );
}
